import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const SVG_SIZE = 400;
const INNER_RADIUS = 60;
const LABEL_OFFSET = 14;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  chartContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '10px 14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '220px',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
  },
};

export default function RadialBarChart({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Sort by avgPrice descending so highest is at 12 o'clock
    const sorted = [...data].sort((a, b) => b.avgPrice - a.avgPrice);
    const n = sorted.length;

    const cx = SVG_SIZE / 2;
    const cy = SVG_SIZE / 2;
    const maxRadius = (SVG_SIZE / 2) * 0.82;

    svg
      .attr('viewBox', `0 0 ${SVG_SIZE} ${SVG_SIZE}`)
      .attr('width', '100%')
      .attr('height', '100%')
      .style('max-width', `${SVG_SIZE}px`);

    const g = svg.append('g').attr('transform', `translate(${cx}, ${cy})`);

    // Price scale: maps avgPrice to outer radius
    const priceExtent = d3.extent(sorted, (d) => d.avgPrice);
    const priceMin = priceExtent[0];
    const priceMax = priceExtent[1];
    const radiusScale = d3
      .scaleLinear()
      .domain([0, priceMax])
      .range([INNER_RADIUS, maxRadius]);

    // Color scale: green (cheapest) -> blue -> red (most expensive)
    const colorScale = d3
      .scaleLinear()
      .domain(
        priceMin === priceMax
          ? [priceMin, priceMax + 1]
          : [priceMin, (priceMin + priceMax) / 2, priceMax]
      )
      .range(
        priceMin === priceMax
          ? ['#22c55e', '#3b82f6']
          : ['#22c55e', '#3b82f6', '#ef4444']
      )
      .interpolate(d3.interpolateRgb);

    // Angle for each state wedge
    const anglePerState = (2 * Math.PI) / n;
    const padAngle = 0.02;

    // Reference circles at $40, $45, $50, $55
    const refPrices = [40, 45, 50, 55];
    refPrices.forEach((price) => {
      const r = radiusScale(price);
      if (r > INNER_RADIUS && r <= maxRadius) {
        g.append('circle')
          .attr('cx', 0)
          .attr('cy', 0)
          .attr('r', r)
          .attr('fill', 'none')
          .attr('stroke', '#e5e7eb')
          .attr('stroke-dasharray', '3,3')
          .attr('stroke-width', 0.8);

        // Label at top of reference circle
        g.append('text')
          .attr('x', 0)
          .attr('y', -r)
          .attr('dy', '-3')
          .attr('text-anchor', 'middle')
          .attr('font-size', '8px')
          .attr('fill', '#c0c4cc')
          .text(`$${price}`);
      }
    });

    // Inner circle baseline
    g.append('circle')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', INNER_RADIUS)
      .attr('fill', '#fafbfc')
      .attr('stroke', '#e5e7eb')
      .attr('stroke-width', 0.8);

    // Draw arcs
    const arcGen = d3.arc();

    sorted.forEach((d, i) => {
      const startAngle = i * anglePerState - Math.PI / 2 + padAngle / 2;
      const endAngle = (i + 1) * anglePerState - Math.PI / 2 - padAngle / 2;
      const outerR = radiusScale(d.avgPrice);
      const color = colorScale(d.avgPrice);
      const rank = i + 1;

      // Main arc
      g.append('path')
        .attr(
          'd',
          arcGen({
            innerRadius: INNER_RADIUS,
            outerRadius: outerR,
            startAngle: startAngle + Math.PI / 2,
            endAngle: endAngle + Math.PI / 2,
          })
        )
        .attr('fill', color)
        .attr('fill-opacity', 0.8)
        .attr('stroke', '#fff')
        .attr('stroke-width', 1)
        .style('cursor', 'pointer')
        .on('mouseenter', function (event) {
          d3.select(this)
            .transition()
            .duration(150)
            .attr('fill-opacity', 1)
            .attr(
              'd',
              arcGen({
                innerRadius: INNER_RADIUS,
                outerRadius: outerR + 5,
                startAngle: startAngle + Math.PI / 2,
                endAngle: endAngle + Math.PI / 2,
              })
            );
          setTooltip({
            x: event.clientX + 14,
            y: event.clientY - 14,
            state: d.state,
            avgPrice: d.avgPrice,
            addressCount: d.addressCount,
            rank,
            total: n,
          });
        })
        .on('mousemove', function (event) {
          setTooltip({
            x: event.clientX + 14,
            y: event.clientY - 14,
            state: d.state,
            avgPrice: d.avgPrice,
            addressCount: d.addressCount,
            rank,
            total: n,
          });
        })
        .on('mouseleave', function () {
          d3.select(this)
            .transition()
            .duration(150)
            .attr('fill-opacity', 0.8)
            .attr(
              'd',
              arcGen({
                innerRadius: INNER_RADIUS,
                outerRadius: outerR,
                startAngle: startAngle + Math.PI / 2,
                endAngle: endAngle + Math.PI / 2,
              })
            );
          setTooltip(null);
        });

      // State label at end of arc
      const midAngle = (startAngle + endAngle) / 2;
      const labelR = outerR + LABEL_OFFSET;
      const lx = labelR * Math.cos(midAngle);
      const ly = labelR * Math.sin(midAngle);

      // Determine text anchor based on angle position
      const angleDeg = (midAngle * 180) / Math.PI;
      let textAnchor = 'middle';
      if (angleDeg > 20 && angleDeg < 160) textAnchor = 'start';
      else if (angleDeg > -160 && angleDeg < -20) textAnchor = 'end';

      g.append('text')
        .attr('x', lx)
        .attr('y', ly)
        .attr('text-anchor', textAnchor)
        .attr('dominant-baseline', 'central')
        .attr('font-size', '9px')
        .attr('font-weight', 600)
        .attr('fill', '#4b5563')
        .text(d.state);
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>State Price Rankings</div>
        <div style={styles.subtitle}>
          Average pest365 price by state in radial layout
        </div>
        <div style={styles.emptyState}>No state pricing data available</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>State Price Rankings</div>
      <div style={styles.subtitle}>
        Average pest365 price by state in radial layout
      </div>
      <div style={styles.chartContainer}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div
            style={{
              fontWeight: 600,
              color: '#1f2937',
              marginBottom: '6px',
              fontSize: '13px',
            }}
          >
            {tooltip.state}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              marginBottom: '2px',
            }}
          >
            <span style={{ color: '#6b7280' }}>Avg Price</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              ${tooltip.avgPrice.toFixed(2)}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              marginBottom: '2px',
            }}
          >
            <span style={{ color: '#6b7280' }}>Addresses</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              {tooltip.addressCount}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
            }}
          >
            <span style={{ color: '#6b7280' }}>Rank</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              #{tooltip.rank} of {tooltip.total}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
